import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import AppModal from "components/ui/AppModal";
import AppInput from "components/ui/AppInput";
import AppSelect from "components/ui/AppSelect";
import AppSearchInput from "components/ui/AppSearchInput";
import AppButton from "components/ui/AppButton";
import AppOutlineButton from "components/ui/AppOutlineButton";
import { UTILS } from "utils";
import { useSelector, useDispatch } from "react-redux";
import { AreasAction } from "redux/actions";
import {
  useAddNewCountryMutation,
  useGetAddedCountriesMutation,
} from "redux/shipping-methods/shipping-service";
import { calculateDeliveryTimeInMins } from "features/shipping_methods/helpers";

const initialState = {
  minOrder: "",
  deliveryFee: "",
  deliveryTime: { Hours: 0, Minutes: 0, Days: 0 },
};

const AddNewCountry = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const [getAddedCountries] = useGetAddedCountriesMutation();
  const [addNewCountry] = useAddNewCountryMutation();
  const { t } = useTranslation();
  const [searchTerm, setSearch] = useState("");
  const { selectedStore, selectedCountries, customizedCountryList } =
    useSelector((state) => state.AreasReducer);
  const { currentLanguage } = useSelector((state) => state.LanguageSlice);
  const { isLoading } = useSelector((state) => state.LoaderReducer);

  const [state, setState] = useState(initialState);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setState(initialState);
    dispatch(AreasAction.updateSelectedCountries([]));
  }, [open, dispatch]);

  const handleValues = async (key, value) => {
    setState({ ...state, [key]: value });
  };
  const handleTimeValues = (key, value) => {
    setState((prev) => ({
      ...prev,
      deliveryTime: {
        ...prev.deliveryTime,
        [key]: Number(value || 0),
      },
    }));
  };

  const addCountries = UTILS.withTryCatch(async () => {
    const delTime = calculateDeliveryTimeInMins({
      deliveryTime: state.deliveryTime,
    });
    const userDetail = UTILS.getCurrentUser();
    let areas = [];
    selectedCountries.forEach((country) => {
      areas.push({
        countryID: country._id,
        deliveryFee: +Number(state.deliveryFee),
        deliveryTime: +Number(delTime),
        minOrder: +Number(state.minOrder),
      });
    });
    const obj = {
      brandId: userDetail.brandId,
      storeId: selectedStore.value,
      areas: areas,
      language: currentLanguage,
    };
    const getObj = {
      brandId: userDetail.brandId,
      storeId: selectedStore.value,
      language: currentLanguage,
    };

    await addNewCountry(obj).unwrap();
    await getAddedCountries(getObj).unwrap();
    dispatch(AreasAction.updateSelectedCountries([]));
    dispatch(AreasAction.setSync());
    onClose();
  });

  const saveArea = (country) => {
    let update = [];
    const isCityAlreadyCountry = !!selectedCountries?.find(
      (c) => c?._id === country?._id
    );
    if (isCityAlreadyCountry) {
      update = selectedCountries.filter((c) => c._id !== country._id);
    } else {
      update = [...selectedCountries, country];
    }
    dispatch(AreasAction.updateSelectedCountries(update));
  };

  const isChecked = (country) => {
    return !!selectedCountries?.find((c) => c?._id === country?._id);
  };
  const COUNTRIES = useMemo(() => {
    const term = searchTerm?.toLowerCase()?.trim() ?? "";
    if (!term) return customizedCountryList || [];
    return (customizedCountryList || []).filter((item) => {
      const nameEn = item?.countryNameEN || "";
      const nameAr = item?.countryNameAR || "";
      return (
        nameEn.toLowerCase().includes(term) || nameAr.toLowerCase().includes(term)
      );
    });
  }, [customizedCountryList, searchTerm]);

  let isValid = () => {
    if (
      isLoading ||
      state.minOrder === "" ||
      state.deliveryFee === "" ||
      selectedCountries?.length <= 0 ||
      (state?.deliveryTime?.Days === 0 &&
        state?.deliveryTime?.Hours === 0 &&
        state?.deliveryTime?.Minutes === 0)
    )
      return false;
    return true;
  };
  const handleInputValue = (key, val) => {
    const value = UTILS.NumberConversion(val);
    handleValues(key, value);
  };
  const daysOptions = Array.from({ length: 8 }, (_, value) => ({
    label: `${value} ${t("Days")}`,
    value,
  }));
  const hourOptions = Array.from({ length: 24 }, (_, value) => ({
    label: `${value} ${t("Hours")}`,
    value,
  }));
  const minuteOptions = Array.from({ length: 12 }, (_, index) => ({
    label: `${index * 5} ${t("Minutes")}`,
    value: index * 5,
  }));

  const footer = (
    <div className="flex justify-end gap-2">
      <AppOutlineButton variant="secondary" onClick={onClose} disabled={isLoading}>
        {t("Cancel")}
      </AppOutlineButton>
      <AppButton
        variant="primary"
        onClick={addCountries}
        disabled={!isValid()}
        loading={isLoading}
      >
        {t("Add Countries")}
      </AppButton>
    </div>
  );

  return (
    <AppModal
      title={t("Add delivery countries")}
      open={open}
      onClose={onClose}
      width={650}
      footer={footer}
      loading={isLoading}
    >
      <div className="grid grid-cols-2 gap-4">
        <AppInput
          label={t("Min. Basket Value")}
          value={state.minOrder}
          onInput={(e) => handleInputValue("minOrder", e.target.value)}
        />
        <AppInput
          label={t("Delivery Fee")}
          value={state.deliveryFee}
          onInput={(e) => handleInputValue("deliveryFee", e.target.value)}
        />
      </div>
      <div className="my-3 text-lg font-bold">{t("Delivery Time")}</div>
      <div className="grid grid-cols-3 gap-4">
        <AppSelect
          placeholder={t("Select Days")}
          value={state.deliveryTime.Days}
          options={daysOptions}
          onChange={(value) => handleTimeValues("Days", value)}
        />
        <AppSelect
          placeholder={t("Select Hours")}
          value={state.deliveryTime.Hours}
          options={hourOptions}
          onChange={(value) => handleTimeValues("Hours", value)}
        />
        <AppSelect
          placeholder={t("Select Minutes")}
          value={state.deliveryTime.Minutes}
          options={minuteOptions}
          onChange={(value) => handleTimeValues("Minutes", value)}
        />
      </div>
      <div className="my-3 text-lg font-bold">
        <span className="mb-2 block">{t("Search Country Name")}</span>
        <AppSearchInput
          value={searchTerm}
          placeholder={t("Search")}
          onInput={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-200 p-2">
        {COUNTRIES?.length === 0 ? (
          <div className="p-2 text-sm text-gray-500">{t("No countries found")}</div>
        ) : (
          COUNTRIES.map((country) => {
            const selected = isChecked(country);
            const name =
              currentLanguage === "EN"
                ? country?.countryNameEN
                : country?.countryNameAR;
            return (
              <button
                key={country?._id}
                type="button"
                className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left hover:bg-gray-50"
                onClick={() => saveArea(country)}
              >
                <div className="text-sm font-medium text-gray-800">{name}</div>
                <span
                  className={`block h-5 w-5 rounded border ${
                    selected ? "bg-blue-500 border-blue-500" : "bg-white border-gray-300"
                  }`}
                />
              </button>
            );
          })
        )}
      </div>
    </AppModal>
  );
};
export default AddNewCountry;
